import { I18nLabelDefaults } from "flexlayout-react";
import type { TFunction } from "i18next";
import { ArchiveBoxIcon, ArrowDownTrayIcon, Cog6ToothIcon, ExclamationTriangleIcon, FolderIcon, LinkIcon, PlusIcon, Squares2X2Icon, UserCircleIcon, WrenchScrewdriverIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { APP_SECTIONS, sectionOfPath } from "../../appSections";
import { isAppPath, pathnameOf } from "../workspaceAddress";

/** The library's own words DMW shows, by its key; the rest are never shown (no pop-out, rename, maximize, pinning, tab groups or the library's own menu). */
const LIBRARY_LABELS: Readonly<Record<string, string>> = {
  "flexlayout.ui.close.tab": "shell.tabs.close",
  "flexlayout.ui.overflow.menu.tooltip": "shell.tabs.more",
  "flexlayout.ui.splitter": "shell.tabs.resize",
  "flexlayout.ui.active.tabset": "shell.tabs.activeGroup",
  "flexlayout.ui.move.tabset": "shell.tabs.moveGroup",
  "flexlayout.ui.error.rendering.component": "shell.tabs.failed",
  "flexlayout.ui.error.rendering.component.retry": "shell.tabs.retry",
};

/** Pages outside the four sections, named by their first path segment. */
export const PAGE_LABELS: Readonly<Record<string, { readonly labelKey: string; readonly icon: typeof PlusIcon }>> = {
  administration: { labelKey: "administration.title", icon: Cog6ToothIcon },
  batches: { labelKey: "shell.tabs.page.batch", icon: ArchiveBoxIcon },
  connections: { labelKey: "profile.title", icon: LinkIcon },
  connectors: { labelKey: "profile.pages.connectors", icon: WrenchScrewdriverIcon },
  "get-started": { labelKey: "profile.pages.connectors", icon: WrenchScrewdriverIcon },
  items: { labelKey: "shell.tabs.page.item", icon: Squares2X2Icon },
  "import-proposals": { labelKey: "shell.tabs.page.importProposal", icon: ArrowDownTrayIcon },
  "new-tab": { labelKey: "shell.tabs.newTab", icon: PlusIcon },
  profile: { labelKey: "profile.title", icon: UserCircleIcon },
  projects: { labelKey: "projects.title", icon: FolderIcon },
  "report-a-problem": { labelKey: "shell.tabs.page.problemReport", icon: ExclamationTriangleIcon },
  reviews: { labelKey: "shell.tabs.page.review", icon: ClipboardDocumentCheckIcon },
};

/** A route's name until its page names itself: its section, or the kind of page. */
function routeLabel(route: string, t: TFunction): string {
  const pathname = pathnameOf(route);
  const section = APP_SECTIONS.find(({ id }) => id === sectionOfPath(pathname));
  if (section !== undefined) return t(section.labelKey);
  return t(PAGE_LABELS[pathname.split("/")[1] ?? ""]?.labelKey ?? "app.title");
}

/** Every string the layout shows, in the user's language: a tab's name is its page's title, or its route until the page names itself. */
export function layoutString(key: string, t: TFunction): string {
  const ownKey = LIBRARY_LABELS[key];
  if (ownKey !== undefined) return t(ownKey);
  if (key in I18nLabelDefaults) return I18nLabelDefaults[key] ?? key;
  return isAppPath(key) ? routeLabel(key, t) : key;
}
